import { memo, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import startCase from 'lodash-es/startCase';
import { UseInfiniteQueryResult } from 'react-query';
import { AdaptoActions, FloatingActionBar } from '@frontend/adapto-actions';
import { useCallInsightController } from '@frontend/call-insight-panel';
import { Chips } from '@frontend/chips';
import { EmptyStateConfig, InfinitePaginatedList, RenderListItemProps } from '@frontend/components';
import { useGetDisplayName } from '@frontend/get-display-name';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { CallDetailsContext, PhonesUtils, RecentCallRecord, RecentCallRecords } from '@frontend/phone-panels';
import { Photos } from '@frontend/photos';
import { useAppScopeStore, useWeTags } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Text, PhoneOutgoingIcon, Chip, ListRow, Tabs, Truncated } from '@frontend/design-system';
import { CallHistoryActionBar } from './call-history-action-bar';
import { CallStatusTabs } from './constants';
import { HistoryActionsGetterProps } from './history-actions';
import { getAiOutcomeIcon } from './ui-utils';
import { VoicemailButton } from './voicemail-button';

export const SmallView = memo(
  ({
    hasVoiceAi,
    showCallIntelInsights,
    getHistoryActions,
    selectedLocationIds,
    infiniteQueryProps,

    listHeight,
    activeTab,
    onTabChange,
  }: {
    hasVoiceAi: boolean;
    showCallIntelInsights: boolean;
    getHistoryActions: (props: HistoryActionsGetterProps) => (React.ReactElement | null)[];
    selectedLocationIds: string[];
    infiniteQueryProps: UseInfiniteQueryResult<RecentCallRecords, unknown>;

    emptyState: EmptyStateConfig;

    listHeight?: number;
    panelProps: PanelProps;
    activeTab: string;
    onTabChange: (tab: string) => void;
  }) => {
    const { t } = useTranslation('calls');
    const showMissedAbandonedTab = useWeTags('missed-abandoned-tabs');
    const isMultiLocationSelected = selectedLocationIds.length > 1;
    const [selectedIndex, setSelectedIndex] = useState(-1);

    const allDataRows = useMemo(() => {
      if (!infiniteQueryProps.data?.pages) return [];
      return infiniteQueryProps.data.pages.flatMap((page) => page.data || []);
    }, [infiniteQueryProps]);

    const renderListItem = useMemo(
      () =>
        getRenderListItem({
          hasVoiceAi,
          showCallIntelInsights,
          getHistoryActions,
          setSelectedIndex,
          shouldShowLocationBadge: selectedLocationIds.length > 1,
        }),
      [getHistoryActions, selectedLocationIds.length, hasVoiceAi, showCallIntelInsights]
    );

    useLayoutEffect(() => {
      if (selectedIndex > -1 && selectedIndex < allDataRows.length) {
        const element = document.querySelector(`li[data-index="${selectedIndex}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }, [allDataRows.length, selectedIndex]);

    return (
      <div>
        {isMultiLocationSelected && (
          <div
            css={{
              padding: theme.spacing(1, 2, 2),
              display: 'grid',
              gap: theme.spacing(2),
              borderBottom: !showMissedAbandonedTab ? `1px solid ${theme.colors.neutral20}` : 'none',
            }}
          >
            <CallHistoryActionBar />
            {/* <SwitchField {...switchField} label={t('View Only Missed/Abandoned Calls')} name='toggle' /> */}
          </div>
        )}
        <div>
          {showMissedAbandonedTab ? (
            <Tabs initialTab={CallStatusTabs.ALL_CALL_RECORDS} onChange={onTabChange} controlledTab={activeTab}>
              <Tabs.Bar fullWidth={false}>
                <Tabs.Tab id={CallStatusTabs.ALL_CALL_RECORDS}>{t('All')}</Tabs.Tab>
                <Tabs.Tab id={CallStatusTabs.MISSED_AND_ABANDONED_CALLS}>{t('Missed / Abandoned')}</Tabs.Tab>
              </Tabs.Bar>
              <InfinitePaginatedList
                height={listHeight}
                infiniteQueryProps={infiniteQueryProps}
                offsetHeightProp={!isMultiLocationSelected ? 8 : 0}
                renderListItem={renderListItem}
              />
            </Tabs>
          ) : (
            <InfinitePaginatedList
              height={listHeight}
              infiniteQueryProps={infiniteQueryProps}
              renderListItem={renderListItem}
            />
          )}
        </div>
      </div>
    );
  }
);
SmallView.displayName = 'HistorySmallView';

type PanelProps = {
  hasAccessToRecordings: boolean;
  callDetailsQueryKey: string[];
  sectionFocus: CallDetailsContext['sectionFocus'];
};

const getRenderListItem =
  ({
    getHistoryActions,
    setSelectedIndex,
    shouldShowLocationBadge,
    hasVoiceAi,
    showCallIntelInsights,
  }: {
    getHistoryActions: (props: HistoryActionsGetterProps) => (React.ReactElement | null)[];
    setSelectedIndex: (index: number) => void;
    shouldShowLocationBadge: boolean;
    hasVoiceAi: boolean;
    showCallIntelInsights: boolean;
  }) =>
  // eslint-disable-next-line react/display-name
  ({ listItem, index }: RenderListItemProps<RecentCallRecord>) => {
    return (
      <CallHistoryListItem
        hasVoiceAi={hasVoiceAi}
        showCallIntelInsights={showCallIntelInsights}
        listItem={listItem}
        index={index}
        getHistoryActions={getHistoryActions}
        setSelectedIndex={setSelectedIndex}
        shouldShowLocationBadge={shouldShowLocationBadge}
      />
    );
  };

const CallHistoryListItem = memo(
  ({
    hasVoiceAi,
    showCallIntelInsights,
    listItem,
    index,
    shouldShowLocationBadge,
    getHistoryActions,
    setSelectedIndex,
  }: {
    hasVoiceAi: boolean;
    showCallIntelInsights: boolean;
    listItem: RecentCallRecord;
    index: number;
    shouldShowLocationBadge: boolean;
    getHistoryActions: (props: HistoryActionsGetterProps) => (React.ReactElement | null)[];
    setSelectedIndex: (idx: number) => void;
  }) => {
    const { t } = useTranslation('calls');
    const { getScopeName } = useAppScopeStore();
    const showCompactListRow = useWeTags('compact-list-row');
    const person = listItem.person;
    const { firstName, lastName, fullName } = useGetDisplayName({
      person: {
        firstName: person?.firstName || '',
        lastName: person?.lastName || '',
        preferredName: person?.preferredName,
      },
    });

    const { open: openCallInsightPanel } = useCallInsightController({
      source: 'callHistory',
    });

    const showPanel = useCallback(() => {
      openCallInsightPanel({ hydratedCall: listItem }, { trackingContext: { source: 'recent-calls-table' } });
      setSelectedIndex(index);
    }, [index, listItem, openCallInsightPanel, setSelectedIndex]);

    const { actions } = useMemo(() => {
      const allActions = getHistoryActions({
        index,
        rowData: listItem,
      });
      allActions.splice(
        2,
        0,
        <AdaptoActions.Action
          icon='document-filled'
          key='view-details-action'
          onClick={showPanel}
          label={t('Call Insights')}
          trackingId={
            showCallIntelInsights
              ? 'phone-portal-weave2.0-calls-btn-tablerowaction-call-insight-with-callintel'
              : 'phone-portal-weave2.0-voicemails-view-details'
          }
        />
      );

      return { actions: allActions };
    }, [getHistoryActions, index, listItem, showCallIntelInsights, showPanel, t]);

    const outcome = listItem.aiMetadata?.aiOutcome;

    const phoneNumber = listItem.direction === 'inbound' ? listItem.callerNumber : listItem.dialedNumber;
    const formattedPhoneNumber = formatPhoneNumber(phoneNumber);

    const getNameplateAdornment = () => {
      if (listItem.direction === 'outbound') {
        return <PhoneOutgoingIcon size={16} />;
      } else if (outcome && hasVoiceAi) {
        return getAiOutcomeIcon({ useTooltip: true, outcome });
      } else {
        return null;
      }
    };

    if (showCompactListRow) {
      return (
        <FloatingActionBar>
          <FloatingActionBar.Actions>{actions}</FloatingActionBar.Actions>
          <FloatingActionBar.Content>
            <ListRow css={{ padding: theme.spacing(1.5), cursor: 'pointer' }} onClick={showPanel}>
              <ListRow.Lead>
                <Photos.ContactProfilePhoto
                  css={{ gridArea: 'photo', gridRow: 'first' }}
                  locationId={listItem.locationId}
                  personId={listItem.person?.personId || ''}
                  name={fullName}
                  disableClick={!listItem.person?.personId}
                />
              </ListRow.Lead>
              <ListRow.Content>
                <ListRow.Content.Title
                  css={{
                    whiteSpace: 'pre-line',
                    fontWeight: 'inherit',
                    display: 'flex',
                  }}
                  isCompactMode
                >
                  <NamePlate
                    firstName={firstName}
                    lastName={lastName}
                    potentialName={listItem.callerName && !isNaN(+listItem?.callerName) ? '' : listItem?.callerName}
                    fallback={formattedPhoneNumber || t('Unknown')}
                    css={{ gridArea: 'name', gridRow: 'first' }}
                    adornment={getNameplateAdornment()}
                    size='large'
                  />
                </ListRow.Content.Title>
                <div
                  css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), margin: theme.spacing(1, 0) }}
                >
                  {shouldShowLocationBadge && listItem.locationId && (
                    <Chips.LocationChip
                      variant='default'
                      css={{ fontSize: theme.fontSize(12), width: 20, padding: theme.spacing(0.5), gap: 0, height: 20 }}
                      tooltipLabel={getScopeName(listItem.locationId)}
                    ></Chips.LocationChip>
                  )}
                  <Chip
                    variant='default'
                    size='tiny'
                    css={{ fontSize: theme.fontSize(12), width: 20, padding: theme.spacing(0.5), gap: 0, height: 20 }}
                    tooltipLabel={formattedPhoneNumber}
                    leftElement={<Icon name='pound' size={12} />}
                  />
                  {listItem.direction === 'inbound' && listItem.status && (
                    <Chip variant={PhonesUtils.getStatusChipColor(listItem.status)} size='tiny'>
                      {startCase(listItem.status)}
                    </Chip>
                  )}
                  {listItem.direction === 'inbound' && listItem.status === 'missed' && listItem.hasVoicemail && (
                    <VoicemailButton callRecord={listItem} size='small' stopPropagation />
                  )}
                </div>
              </ListRow.Content>
              <ListRow.Trail
                verticalAlign='flex-start'
                css={{
                  alignSelf: 'start',
                  '&& .trailing-column-child': {
                    fontSize: `${theme.fontSize(12)} !important`,
                    marginTop: theme.spacing(0.25),
                    lineHeight: '20px',
                  },
                }}
              >
                <Text size='small' color='subdued' css={{ whiteSpace: 'nowrap' }}>
                  {dayjs(listItem.startedAt).format('MMM DD, hh:mm A')}
                </Text>
              </ListRow.Trail>
            </ListRow>
          </FloatingActionBar.Content>
        </FloatingActionBar>
      );
    }

    return (
      <div
        css={{
          display: 'grid',
          padding: theme.spacing(2),
          gridTemplateAreas: '"photo name actions" ". details details" ". meta meta"',
          gridTemplateColumns: 'auto 1fr auto',
          gridTemplateRows:
            '[first-start] auto [first-end] 0px [second-start] auto [second-end] 8px [third-start] auto [third-end]',
          columnGap: theme.spacing(1),
          alignItems: 'center',
          borderBottom: `1px solid ${theme.colors.neutral20}`,
        }}
      >
        <Photos.ContactProfilePhoto
          css={{ gridArea: 'photo', gridRow: 'first' }}
          locationId={listItem.locationId}
          personId={listItem.person?.personId || ''}
          name={fullName}
          disableClick={!listItem.person?.personId}
        >
          {/* {!listItem.readAt && <Avatar.Dot variant='primary' />} */}
        </Photos.ContactProfilePhoto>
        <NamePlate
          firstName={firstName}
          lastName={lastName}
          potentialName={listItem.callerName && !isNaN(+listItem?.callerName) ? '' : listItem?.callerName}
          fallback={formattedPhoneNumber || t('Unknown')}
          css={{ gridArea: 'name', gridRow: 'first' }}
          adornment={getNameplateAdornment()}
        />
        {actions.length > 0 && (
          <div style={{ gridArea: 'actions' }}>
            <AdaptoActions iconColor='inherit' maxActions={3} css={{ gap: 0 }}>
              {actions}
            </AdaptoActions>
          </div>
        )}
        <div
          css={{
            gridArea: 'details',
            display: 'grid',
            alignItems: 'center',
            gap: theme.spacing(1),
            gridRow: 'second',
          }}
        >
          <div
            css={{
              display: 'grid',
              gridTemplateColumns: 'max-content max-content max-content',
              alignItems: 'center',
              gap: theme.spacing(1),
            }}
          >
            <Text size='medium'>{formattedPhoneNumber}</Text>
          </div>
        </div>
        <div
          css={{
            gridArea: 'meta',
            gridRow: 'third',
            display: 'grid',
            gridTemplateColumns: 'max-content max-content max-content',
            alignItems: 'center',
            gap: theme.spacing(1),
          }}
        >
          {listItem.direction === 'inbound' && listItem.status && (
            <Chip css={{ width: 'max-content' }} variant={PhonesUtils.getStatusChipColor(listItem.status)}>
              {startCase(listItem.status)}
            </Chip>
          )}
          {listItem.direction === 'inbound' && listItem.status === 'missed' && listItem.hasVoicemail && (
            <VoicemailButton callRecord={listItem} size='small' stopPropagation />
          )}
          {shouldShowLocationBadge && listItem.locationId && (
            <Chips.LocationChip>{getScopeName(listItem.locationId)}</Chips.LocationChip>
          )}
        </div>
      </div>
    );
  }
);

CallHistoryListItem.displayName = 'CallHistoryListItem';

const NamePlate = ({
  firstName,
  lastName,
  potentialName,
  fallback,
  adornment,
  className,
  size = 'medium',
}: {
  firstName?: string;
  lastName?: string;
  potentialName?: string;
  fallback?: string;
  adornment?: React.ReactNode;
  className?: string;
  size?: 'medium' | 'large';
}) => {
  const { t } = useTranslation('calls');
  let content;
  const hasDefinedName = firstName || lastName;

  if (hasDefinedName) {
    content = (
      <Truncated as='div'>
        {!!firstName && (
          <Text as='span' size={size} weight='bold' css={{ color: 'currentcolor' }}>
            {firstName}
          </Text>
        )}

        {firstName && lastName ? ' ' : null}

        {!!lastName && (
          <Text as='span' size={size} css={{ color: 'currentcolor' }}>
            {lastName}
          </Text>
        )}
      </Truncated>
    );
  } else if (potentialName) {
    content = (
      <Truncated as={Text} css={{ fontStyle: 'italic', color: 'currentcolor' }} size={size}>
        {potentialName}
      </Truncated>
    );
  } else {
    content = (
      <Truncated as={Text} css={{ color: 'currentcolor' }} size={size}>
        {fallback || t('Unknown')}
      </Truncated>
    );
  }

  return (
    <div
      className={className}
      css={{
        display: 'grid',
        ...(adornment ? { gridTemplateColumns: 'auto 1fr' } : { gridTemplateColumns: '1fr' }),
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
      }}
    >
      {content}
      {adornment}
    </div>
  );
};
